import * as React from 'react';
import { DndContext, DragEndEvent, DragOverlay, useDraggable, useDroppable } from '@dnd-kit/core';
import { Profile, ProfileType, HcpGroup } from '@ixicare/resources';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table } from '@ixicare/ui';
import { t } from '~/i18n';
import { useStores } from '~store/utils';

export const ResidentAssignmentTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { notifications } = useStores();
  const [activeDragId, setActiveDragId] = React.useState<string | null>(null);

  // Fetch all groups
  const { data: groups } = useQuery({
    queryKey: ['hcpGroups'],
    queryFn: () => HcpGroup.getHcpGroups(),
  });

  // Fetch all residents
  const { data: allResidents } = useQuery({
    queryKey: ['residents'],
    queryFn: () => Profile.getProfiles({ profileType: ProfileType.Resident }),
  });

  // Mutation to assign resident to group
  const assignMutation = useMutation({
    mutationFn: ({ groupId, residentId }: { groupId: string; residentId: number }) =>
      HcpGroup.assignHcpToHcpGroup(groupId, residentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hcpGroups'] });
      queryClient.invalidateQueries({ queryKey: ['residents'] });
      notifications.addUserActionNotification({
        type: 'success' as any,
        notificationType: 'success',
        message: t('hcpGroups.assign.resident.success'),
      });
    },
  });

  // Mutation to unassign resident from group
  const unassignMutation = useMutation({
    mutationFn: ({ groupId, residentId }: { groupId: string; residentId: number }) =>
      HcpGroup.unassignHcpFromHcpGroup(groupId, residentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hcpGroups'] });
      queryClient.invalidateQueries({ queryKey: ['residents'] });
      notifications.addUserActionNotification({
        type: 'success' as any,
        notificationType: 'success',
        message: t('hcpGroups.unassign.resident.success'),
      });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!over) return;

    const residentId = parseInt(active.id as string);
    const targetId = over.id as string;
    const resident = allResidents?.find((r) => r.id === residentId);

    if (targetId === 'unassigned' && resident?.hcpGroupId) {
      unassignMutation.mutate({ groupId: resident.hcpGroupId, residentId });
    } else if (targetId.startsWith('group-')) {
      const groupId = targetId.replace('group-', '');
      assignMutation.mutate({ groupId, residentId });
    }
  };

  // Get residents assigned to each group
  const getGroupResidents = (groupId: string) => {
    if (!allResidents) return [];
    return allResidents.filter((resident) => resident.hcpGroupId === groupId);
  };

  // Get unassigned residents (those without a group)
  const unassignedResidents = React.useMemo(() => {
    if (!allResidents) return [];
    return allResidents.filter((resident) => !resident.hcpGroupId);
  }, [allResidents]);

  return (
    <DndContext onDragEnd={handleDragEnd} onDragStart={(e) => setActiveDragId(e.active.id as string)}>
      <div>
        <p style={{ marginBottom: '20px', color: '#666' }}>{t('hcpGroups.tabs.assignResidents.description')}</p>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeadCol>{t('hcpGroups.table.groupName')}</Table.HeadCol>
              <Table.HeadCol>{t('hcpGroups.assigned.residents')}</Table.HeadCol>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {Array.isArray(groups) &&
              groups.map((group) => (
                <Table.Row key={group.id}>
                  <Table.Col>{group.hcpGroupName}</Table.Col>
                  <Table.Col>
                    <GroupDropZone
                      groupId={group.id}
                      assignedResidents={getGroupResidents(group.id)}
                      onRemove={(residentId) => unassignMutation.mutate({ groupId: group.id, residentId })}
                    />
                  </Table.Col>
                </Table.Row>
              ))}
          </Table.Body>
        </Table>

        <UnassignedDropZone unassignedResidents={unassignedResidents} />
      </div>
      <DragOverlay>
        {activeDragId && (
          <div
            style={{
              padding: '8px 16px',
              background: '#fff',
              border: '2px solid #ab47bc',
              borderRadius: '16px',
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
            }}
          >
            {allResidents?.find((r) => r.id === parseInt(activeDragId))?.firstName}{' '}
            {allResidents?.find((r) => r.id === parseInt(activeDragId))?.lastName}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

// Droppable unassigned zone
function UnassignedDropZone({ unassignedResidents }: { unassignedResidents: any[] }) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'unassigned',
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        marginTop: '30px',
        padding: '20px',
        border: `1px ${isOver ? 'solid' : 'dashed'} ${isOver ? '#f44336' : '#ccc'}`,
        borderRadius: '4px',
        backgroundColor: isOver ? '#ffebee' : 'transparent',
      }}
    >
      <h4>{t('hcpGroups.unassigned.residents')}</h4>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '15px' }}>
        {unassignedResidents.map((resident) => (
          <DraggableResident key={resident.id} resident={resident} />
        ))}
      </div>
    </div>
  );
}

// Droppable group zone
function GroupDropZone({
  groupId,
  assignedResidents,
  onRemove,
}: {
  groupId: string;
  assignedResidents: any[];
  onRemove: (residentId: number) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `group-${groupId}`,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        minHeight: '40px',
        padding: '10px',
        border: `2px ${isOver ? 'solid' : 'dashed'} ${isOver ? '#4CAF50' : '#ccc'}`,
        borderRadius: '4px',
        backgroundColor: isOver ? '#e8f5e9' : 'transparent',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center',
      }}
    >
      {assignedResidents.length === 0 ? (
        <span style={{ color: '#999' }}>{t('hcpGroups.dropzone.residents')}</span>
      ) : (
        assignedResidents.map((resident) => (
          <DraggableResident key={resident.id} resident={resident} onRemove={() => onRemove(resident.id)} />
        ))
      )}
    </div>
  );
}

// Draggable resident chip
function DraggableResident({ resident, onRemove }: { resident: any; onRemove?: () => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: resident.id.toString(),
  });

  const style: React.CSSProperties = {
    padding: '8px 16px',
    background: isDragging ? '#c8e6c9' : '#f3e5f5',
    border: '1px solid #ce93d8',
    borderRadius: '12px',
    cursor: 'grab',
    opacity: isDragging ? 0.5 : 1,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {resident.firstName} {resident.lastName}
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            fontSize: '16px',
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}
